<template>
  <div>
    <nav-menu />
    <div class="w-full bg-gray-50 border-b border-gray-200">
      <div class="px-6 py-4">
        <div class="flex items-center">
          <div>
            <h1 class="text-lg font-semibold text-gray-900">Fleet Management</h1>
            <p class="text-sm text-gray-500 mt-0.5">Manage vehicles and assign testers</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Overview Cards -->
    <div class="px-6 py-6">
      <div class="grid grid-cols-4 gap-4 mb-6">
        <div class="bg-white rounded-lg shadow p-4">
          <div class="text-sm text-gray-500">Total Vehicles</div>
          <div class="text-2xl font-bold">{{ vehicles.length }}</div>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <div class="text-sm text-gray-500">Online</div>
          <div class="text-2xl font-bold text-green-600">{{ onlineCount }}</div>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <div class="text-sm text-gray-500">Offline</div>
          <div class="text-2xl font-bold text-gray-500">{{ offlineCount }}</div>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <div class="text-sm text-gray-500">Idle</div>
          <div class="text-2xl font-bold text-yellow-600">{{ idleCount }}</div>
        </div>
      </div>

      <!-- Vehicle Table -->
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <table class="min-w-full">
          <thead>
            <tr class="bg-secondary text-gray-300 text-sm font-normal">
              <th class="py-3 px-4 text-left">Vehicle</th>
              <th class="py-3 px-4 text-left">Device ID</th>
              <th class="py-3 px-4 text-center">Status</th>
              <th class="py-3 px-4 text-left">Assigned Tester</th>
              <th class="py-3 px-4 text-left">Starting Location</th>
              <th class="py-3 px-4 text-left">Region</th>
              <th class="py-3 px-4 text-left">Last Position</th>
              <th class="py-3 px-4 text-left">Last Update</th>
              <th class="py-3 px-4 text-center">Tracker</th>
              <th class="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="vehicle in vehicles" :key="vehicle.id" class="h-12 bg-gray-50 hover:bg-gray-100">
              <td class="px-4 border-b border-gray-200 text-left font-bold text-sm">
                {{ vehicle.name || vehicle.id }}
              </td>
              <td class="px-4 border-b border-gray-200 text-left text-sm text-gray-500">
                {{ vehicle.deviceId || '-' }}
              </td>
              <td class="px-4 border-b border-gray-200 text-center">
                <span
                  :class="[
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                    vehicleStatusClass(vehicle)
                  ]"
                >
                  <span :class="['w-1.5 h-1.5 rounded-full mr-1.5', vehicleStatusDot(vehicle)]"></span>
                  {{ vehicleStatusLabel(vehicle) }}
                </span>
              </td>
              <td class="px-4 border-b border-gray-200 text-left">
                <select
                  class="h-8 bg-gray-700 rounded text-white text-sm px-2 w-48"
                  :value="vehicle.assignedUserId || ''"
                  @change="handleAssignment(vehicle, $event.target.value)"
                >
                  <option value="">Unassigned</option>
                  <option v-for="user in testers" :key="user.id" :value="user.id">
                    {{ user.name }}
                  </option>
                </select>
              </td>
              <td class="px-4 border-b border-gray-200 text-left text-sm relative">
                <div v-if="editingStartLocation === vehicle.id" class="relative">
                  <input
                    type="text"
                    v-model="startLocationQuery"
                    @input="debounceGeocode"
                    placeholder="Search address..."
                    class="h-8 bg-gray-100 border border-gray-300 rounded text-sm px-2 w-56 outline-none"
                    ref="startLocationInput"
                  />
                  <button @click="editingStartLocation = null" class="ml-1 text-gray-400 hover:text-gray-600 text-xs">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                  <div v-if="geocodeResults.length" class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 w-72 max-h-48 overflow-y-auto">
                    <div
                      v-for="(result, idx) in geocodeResults"
                      :key="idx"
                      @click="selectStartLocation(vehicle, result)"
                      class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-xs border-b border-gray-100 last:border-0"
                    >
                      {{ result.place_name }}
                    </div>
                  </div>
                </div>
                <div v-else class="flex items-center">
                  <span v-if="getDriverStartLocation(vehicle)" class="text-gray-700 truncate max-w-xs" :title="getDriverStartLocation(vehicle).address">
                    {{ getDriverStartLocation(vehicle).address }}
                  </span>
                  <span v-else class="text-gray-400">Default (Yatala)</span>
                  <button
                    v-if="vehicle.assignedUserId"
                    @click="openStartLocationEditor(vehicle)"
                    class="ml-2 text-gray-400 hover:text-primary text-xs flex-shrink-0"
                    title="Edit starting location"
                  >
                    <i class="fa-solid fa-pencil"></i>
                  </button>
                  <button
                    v-if="getDriverStartLocation(vehicle)"
                    @click="clearStartLocation(vehicle)"
                    class="ml-1 text-gray-400 hover:text-red-500 text-xs flex-shrink-0"
                    title="Remove starting location"
                  >
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </td>
              <td class="px-4 border-b border-gray-200 text-left text-sm text-gray-500">
                <span v-if="getDriverRegion(vehicle)">{{ getDriverRegion(vehicle) }}</span>
                <span v-else class="text-gray-400">-</span>
              </td>
              <td class="px-4 border-b border-gray-200 text-left text-sm text-gray-500">
                <span v-if="vehicle.latitude">
                  {{ vehicle.latitude.toFixed(4) }}, {{ vehicle.longitude.toFixed(4) }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="px-4 border-b border-gray-200 text-left text-sm text-gray-500">
                {{ formatTime(vehicle.lastUpdate) }}
              </td>
              <td class="px-4 border-b border-gray-200 text-center">
                <button
                  @click="toggleTrackerVisibility(vehicle)"
                  :title="vehicle.hiddenFromTracker ? 'Hidden from tracker - click to show' : 'Visible on tracker - click to hide'"
                  :class="[
                    'w-8 h-8 rounded-full inline-flex items-center justify-center text-sm transition-colors',
                    vehicle.hiddenFromTracker
                      ? 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  ]"
                >
                  <i :class="['fa-solid', vehicle.hiddenFromTracker ? 'fa-eye-slash' : 'fa-eye']"></i>
                </button>
              </td>
              <td class="px-4 border-b border-gray-200 text-center space-x-2">
                <button
                  @click="openDetailModal(vehicle)"
                  class="bg-primary hover:bg-blue-700 text-white text-sm font-bold py-1 px-3 rounded"
                >
                  Details
                </button>
                <button
                  @click="confirmRemoveVehicle(vehicle)"
                  class="bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-1 px-3 rounded"
                >
                  Remove
                </button>
              </td>
            </tr>
            <tr v-if="vehicles.length === 0">
              <td colspan="9" class="py-8 text-center text-gray-400">
                No vehicles found. Deploy Cloud Functions to populate vehicle data from Traccar.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Vehicle Detail Modal -->
    <div v-if="showDetailModal" class="fixed z-50 inset-0 flex items-center justify-center">
      <div class="absolute inset-0 z-10 bg-gray-500 opacity-75" @click="showDetailModal = false"></div>
      <div class="bg-white z-20 rounded-lg shadow-lg p-6 w-full max-w-lg">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold">{{ detailVehicle.name || detailVehicle.id }}</h2>
          <span @click="showDetailModal = false" class="text-gray-400 hover:text-gray-600 cursor-pointer text-2xl">&times;</span>
        </div>

        <!-- Mini Map -->
        <div v-if="detailVehicle.latitude" ref="detailMap" class="w-full h-48 rounded mb-4 bg-gray-200"></div>

        <!-- Vehicle Info -->
        <div class="grid grid-cols-2 gap-3 mb-4">
          <div>
            <div class="text-xs text-gray-500">Device ID</div>
            <div class="text-sm font-bold">{{ detailVehicle.deviceId || '-' }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500">Status</div>
            <div class="text-sm font-bold">{{ vehicleStatusLabel(detailVehicle) }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500">Speed</div>
            <div class="text-sm font-bold">{{ Math.round(detailVehicle.speed || 0) }} km/h</div>
          </div>
          <div>
            <div class="text-xs text-gray-500">Assigned To</div>
            <div class="text-sm font-bold">{{ detailVehicle.assignedUserName || 'Unassigned' }}</div>
          </div>
        </div>

        <!-- Recent Events -->
        <div class="mb-4">
          <h3 class="text-sm font-bold text-gray-600 mb-2">Recent Events</h3>
          <div v-if="detailEvents.length === 0" class="text-xs text-gray-400">No recent events</div>
          <div v-for="event in detailEvents" :key="event.id" class="flex items-center text-xs py-1 border-b border-gray-100">
            <span :class="['w-2 h-2 rounded-full mr-2', eventDotClass(event.type)]"></span>
            <span class="text-gray-600">{{ event.type }}</span>
            <span class="ml-auto text-gray-400">{{ formatTime(event.timestamp) }}</span>
          </div>
        </div>

        <!-- Driver Score -->
        <div v-if="detailDriverScore" class="bg-gray-900 rounded-lg p-3">
          <driver-score-card :score="detailDriverScore.score" :metrics="detailDriverScore.metrics" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import NavMenu from "@/components/headers/navMenu";
import DriverScoreCard from "@/components/cards/driverScoreCard";
import {
  pollVehicles,
  getUsers,
  assignVehicleToUser,
  unassignVehicle,
  getVehicleEvents,
  getDriverScores,
  removeVehicle,
  setFire
} from "../firestore/utils";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

export default {
  components: { NavMenu, DriverScoreCard },
  data() {
    return {
      vehicles: [],
      testers: [],
      unsubscribe: null,
      showDetailModal: false,
      detailVehicle: {},
      detailEvents: [],
      detailDriverScore: null,
      detailMap: null,
      editingStartLocation: null,
      startLocationQuery: '',
      geocodeResults: [],
      geocodeTimer: null,
      availableRegions: [],
    };
  },
  computed: {
    onlineCount() {
      return this.vehicles.filter(v => v.online).length;
    },
    offlineCount() {
      return this.vehicles.filter(v => !v.online).length;
    },
    idleCount() {
      return this.vehicles.filter(v => v.online && (v.speed || 0) < 2).length;
    }
  },
  methods: {
    vehicleStatusLabel(vehicle) {
      if (!vehicle.online) return 'Offline';
      if ((vehicle.speed || 0) < 2) return 'Idle';
      return 'Moving';
    },
    vehicleStatusClass(vehicle) {
      if (!vehicle.online) return 'bg-gray-200 text-gray-600';
      if ((vehicle.speed || 0) < 2) return 'bg-yellow-100 text-yellow-800';
      return 'bg-green-100 text-green-800';
    },
    vehicleStatusDot(vehicle) {
      if (!vehicle.online) return 'bg-gray-400';
      if ((vehicle.speed || 0) < 2) return 'bg-yellow-400';
      return 'bg-green-400';
    },
    eventDotClass(type) {
      const map = {
        overspeed: 'bg-red-500',
        hardBrake: 'bg-orange-500',
        ignitionOn: 'bg-green-500',
        ignitionOff: 'bg-gray-500',
        geofenceEnter: 'bg-blue-500',
        geofenceExit: 'bg-purple-500'
      };
      return map[type] || 'bg-gray-400';
    },
    formatTime(timestamp) {
      if (!timestamp) return 'Never';
      const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return diffMin + ' min ago';
      if (diffMin < 1440) return Math.floor(diffMin / 60) + ' hr ago';
      return d.toLocaleDateString('en-AU');
    },
    async handleAssignment(vehicle, userId) {
      try {
        if (userId) {
          const user = this.testers.find(u => u.id === userId);
          if (user) {
            await assignVehicleToUser(vehicle.id, userId, user.name);
          }
        } else {
          await unassignVehicle(vehicle.id, vehicle.assignedUserId);
        }
      } catch (e) {
        console.error("Assignment error:", e);
        alert("Failed to assign vehicle. Make sure the vehicle document exists in Firestore.");
      }
    },
    async toggleTrackerVisibility(vehicle) {
      const hidden = !vehicle.hiddenFromTracker;
      try {
        await setFire({ collection: 'vehicles', doc: vehicle.id, body: { hiddenFromTracker: hidden } });
        this.$set(vehicle, 'hiddenFromTracker', hidden);
      } catch (e) {
        console.error('Error toggling tracker visibility:', e);
      }
    },
    async confirmRemoveVehicle(vehicle) {
      const name = vehicle.name || vehicle.id;
      if (!confirm(`Are you sure you want to remove "${name}"? This cannot be undone.`)) return;
      try {
        await removeVehicle(vehicle.id, vehicle.assignedUserId);
      } catch (e) {
        console.error("Remove vehicle error:", e);
        alert("Failed to remove vehicle.");
      }
    },
    async openDetailModal(vehicle) {
      this.detailVehicle = vehicle;
      this.detailEvents = [];
      this.detailDriverScore = null;
      this.showDetailModal = true;

      // Load events and driver score in parallel
      try {
        const [events, scores] = await Promise.all([
          getVehicleEvents(vehicle.id, 10),
          vehicle.assignedUserId ? getDriverScores(vehicle.assignedUserId) : Promise.resolve([])
        ]);
        this.detailEvents = events;
        if (scores.length > 0) {
          this.detailDriverScore = scores[0];
        }
      } catch (e) {
        console.error("Error loading vehicle details:", e);
      }

      // Init mini map after DOM update
      this.$nextTick(() => {
        if (this.$refs.detailMap && vehicle.latitude) {
          this.initDetailMap(vehicle);
        }
      });
    },
    async fetchAvailableRegions() {
      try {
        const snap = await getDocs(query(collection(db, "regions"), orderBy("name")));
        const list = [];
        snap.forEach((d) => { list.push({ ...d.data(), id: d.id }); });
        this.availableRegions = list;
      } catch (e) {
        console.error("Failed to load regions:", e);
      }
    },
    getDriverRegion(vehicle) {
      if (!vehicle.assignedUserId) return null;
      const user = this.testers.find(u => u.id === vehicle.assignedUserId);
      return user && user.region ? user.region : null;
    },
    getDriverStartLocation(vehicle) {
      if (!vehicle.assignedUserId) return null;
      const user = this.testers.find(u => u.id === vehicle.assignedUserId);
      if (user && user.startLocation && user.startLocation.lat && user.startLocation.lng) {
        return user.startLocation;
      }
      return null;
    },
    openStartLocationEditor(vehicle) {
      this.editingStartLocation = vehicle.id;
      this.startLocationQuery = '';
      this.geocodeResults = [];
      this.$nextTick(() => {
        if (this.$refs.startLocationInput) {
          const input = Array.isArray(this.$refs.startLocationInput) ? this.$refs.startLocationInput[0] : this.$refs.startLocationInput;
          if (input) input.focus();
        }
      });
    },
    debounceGeocode() {
      if (this.geocodeTimer) clearTimeout(this.geocodeTimer);
      this.geocodeTimer = setTimeout(() => { this.geocodeAddress(); }, 300);
    },
    async geocodeAddress() {
      const q = this.startLocationQuery.trim();
      if (q.length < 3) { this.geocodeResults = []; return; }
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw&country=au&limit=5`;
        const response = await fetch(url);
        const data = await response.json();
        this.geocodeResults = data.features || [];
      } catch (e) {
        console.error('Geocode error:', e);
        this.geocodeResults = [];
      }
    },
    async selectStartLocation(vehicle, result) {
      const [lng, lat] = result.center;
      const startLocation = { address: result.place_name, lat, lng };
      const userId = vehicle.assignedUserId;
      if (!userId) return;
      try {
        await setFire({ collection: 'users', doc: userId, body: { startLocation } });
        const user = this.testers.find(u => u.id === userId);
        if (user) this.$set(user, 'startLocation', startLocation);
      } catch (e) {
        console.error('Error saving start location:', e);
        alert('Failed to save starting location.');
      }
      this.editingStartLocation = null;
      this.geocodeResults = [];
    },
    async clearStartLocation(vehicle) {
      const userId = vehicle.assignedUserId;
      if (!userId) return;
      try {
        await setFire({ collection: 'users', doc: userId, body: { startLocation: null } });
        const user = this.testers.find(u => u.id === userId);
        if (user) this.$set(user, 'startLocation', null);
      } catch (e) {
        console.error('Error clearing start location:', e);
      }
    },
    initDetailMap(vehicle) {
      if (this.detailMapInstance) {
        this.detailMapInstance.remove();
      }
      mapboxgl.accessToken = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";
      this.detailMapInstance = new mapboxgl.Map({
        container: this.$refs.detailMap,
        style: "mapbox://styles/mapbox/dark-v11",
        center: [vehicle.longitude, vehicle.latitude],
        zoom: 14,
        interactive: false
      });
      new mapboxgl.Marker()
        .setLngLat([vehicle.longitude, vehicle.latitude])
        .addTo(this.detailMapInstance);
    }
  },
  async mounted() {
    // Load testers
    try {
      const users = await getUsers();
      this.testers = users.filter(u => u.role === 'contractor' || u.role === 'admin');
    } catch (e) {
      console.error("Error loading users:", e);
    }

    this.fetchAvailableRegions();

    // Poll vehicles every 10 seconds (replaces real-time listener to reduce Firestore reads)
    this.unsubscribe = pollVehicles((vehicles) => {
      this.vehicles = vehicles;
    }, 10000);
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.detailMapInstance) this.detailMapInstance.remove();
  }
};
</script>
